/**
 * Batched client for GET /api/products/restock-dates (2026-08-22).
 *
 * Product cards each ask for their own SKU; requests within a 120ms window
 * coalesce into ONE call per channel (the endpoint takes up to 300 skus and
 * is 60s-memoized server-side). Cards never fire per-card HTTP requests.
 */
import axios from 'axios';

const API_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:5100'
  : (process.env.REACT_APP_API_URL || '');

const BATCH_WINDOW_MS = 120;
const MAX_SKUS_PER_CALL = 300;

// channel -> { skus: Map<sku, [resolve,...]>, timer }
const pending = new Map();
// channel -> Map<skuUpper, entry|null>  (session cache; cleared on writes)
const resultCache = new Map();

/** Map product store fields to a restock sync channel; null = not covered. */
export function restockChannelForProduct(product) {
  const store = String(product?.store || product?.storeDisplayName || '').toLowerCase();
  if (store.includes('coffee')) return 'centralcoastcoffee';
  if (store.includes('phaser')) return 'phaserfpv';
  return null;
}

async function flush(channel) {
  const bucket = pending.get(channel);
  if (!bucket) return;
  pending.delete(channel);

  const skus = [...bucket.skus.keys()].slice(0, MAX_SKUS_PER_CALL);
  let dates = {};
  try {
    const resp = await axios.get(`${API_URL}/api/products/restock-dates`, {
      params: { channel, skus: skus.join(',') }
    });
    dates = resp.data?.dates || {};
  } catch (err) {
    // Resolve everything null — a card row degrades to "no date", never blocks.
    console.warn('[restockDates] batch fetch failed:', err.message);
  }

  const cache = resultCache.get(channel) || new Map();
  resultCache.set(channel, cache);
  bucket.skus.forEach((resolvers, sku) => {
    const entry = dates[sku] || null;
    cache.set(sku.toUpperCase(), entry);
    resolvers.forEach((resolve) => resolve(entry));
  });
}

/** Resolve {date, source, published} | null for one SKU (batched + cached). */
export function getRestockDate(channel, sku) {
  const skuTrim = String(sku || '').trim();
  if (!channel || !skuTrim || skuTrim.toUpperCase() === 'N/A') return Promise.resolve(null);

  const cached = resultCache.get(channel)?.get(skuTrim.toUpperCase());
  if (cached !== undefined) return Promise.resolve(cached);

  return new Promise((resolve) => {
    let bucket = pending.get(channel);
    if (!bucket) {
      bucket = { skus: new Map(), timer: setTimeout(() => flush(channel), BATCH_WINDOW_MS) };
      pending.set(channel, bucket);
    }
    const resolvers = bucket.skus.get(skuTrim) || [];
    resolvers.push(resolve);
    bucket.skus.set(skuTrim, resolvers);
  });
}

/**
 * Set a restock date; returns the fresh entry for display. The server decides
 * the mode: SKUs with unlinked preorder lines get THOSE updated (source stays
 * 'preorder' — one source per promise), everything else stores a manual
 * estimate. Publishes to Shopify within ~20s either way.
 */
export async function setManualRestockEstimate(channel, sku, date) {
  const resp = await axios.post(`${API_URL}/api/products/restock-estimate`, { channel, sku, date });
  resultCache.delete(channel);
  return { date, source: resp.data?.estimate?.source || 'manual', published: false };
}

/** Clear a manual estimate. */
export async function clearManualRestockEstimate(channel, sku) {
  await axios.delete(`${API_URL}/api/products/restock-estimate`, { data: { channel, sku } });
  resultCache.delete(channel);
}
